// Translation dictionaries. Add a new locale by:
// 1. Adding it to SUPPORTED_LOCALES
// 2. Adding a corresponding entry in `dictionaries`
// 3. Translating all keys in the new dictionary

export const SUPPORTED_LOCALES = ["uk", "en"] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: Locale = "uk"

export const LOCALE_LABELS: Record<Locale, { native: string; flag: string }> = {
  uk: { native: "Українська", flag: "🇺🇦" },
  en: { native: "English", flag: "🇬🇧" },
}

export type Dictionary = {
  // Common
  common: {
    signIn: string
    signOut: string
    signUp: string
    getStarted: string
    loading: string
    save: string
    cancel: string
    delete: string
    edit: string
    create: string
    refresh: string
    back: string
    settings: string
    or: string
  }

  // Home / landing
  home: {
    badge: string
    headlineLine1: string
    headlineLine2: string
    subheadline: string
    ctaPrimary: string
    ctaSecondary: string
    trust1: string
    trust2: string
    trust3: string
    // Mission/concept section
    missionTitle: string
    missionKicker: string
    problemTitle: string
    problemBody: string
    approachTitle: string
    approachBody: string
    diffTitle: string
    diffBody: string
    // Feature cards
    featuresTitle: string
    feature1Title: string
    feature1Desc: string
    feature2Title: string
    feature2Desc: string
    feature3Title: string
    feature3Desc: string
    feature4Title: string
    feature4Desc: string
    feature5Title: string
    feature5Desc: string
    feature6Title: string
    feature6Desc: string
    // How it works
    howTitle: string
    howKicker: string
    step1Title: string
    step1Body: string
    step2Title: string
    step2Body: string
    step3Title: string
    step3Body: string
    step4Title: string
    step4Body: string
    // Closing CTA
    closingTitle: string
    closingBody: string
    closingCta: string
    footer: string
  }

  // Auth
  auth: {
    welcomeBack: string
    welcomeBackSub: string
    createWorkspace: string
    createWorkspaceSub: string
    nameLabel: string
    namePlaceholder: string
    emailLabel: string
    emailPlaceholder: string
    passwordLabel: string
    passwordPlaceholder: string
    passwordPlaceholderRegister: string
    signInBtn: string
    signInLoading: string
    createBtn: string
    createLoading: string
    noAccount: string
    haveAccount: string
    loginError: string
    registerError: string
    featuresAiDirector: string
    featuresAutoAgents: string
    featuresPayAsGo: string
  }

  // Sidebar
  sidebar: {
    platform: string
    overview: string
    directorChat: string
    workspace: string
    knowledgeBase: string
    memory: string
    schedule: string
    integrations: string
    finance: string
    usage: string
    settings: string
    yourAgents: string
    noAgents: string
    startWithDirector: string
    addAgent: string
    workspaceOwner: string
  }

  // Dashboard
  dashboard: {
    welcomeBack: string
    teamReady: string
    teamWaiting: string
    refresh: string
    activeAgents: string
    tasksCompleted: string
    pendingTasks: string
    needApproval: string
    yourTeam: string
    askDirectorMore: string
    requiresApproval: string
    approve: string
    reject: string
    done: string
    pending: string
    approve_short: string
    open: string
    directorReady: string
    directorReadySub: string
    getStartedBadge: string
    buildYourTeam: string
    buildTeamDesc: string
    startWithDirectorBtn: string
    tour1Title: string
    tour1Desc: string
    tour2Title: string
    tour2Desc: string
    tour3Title: string
    tour3Desc: string
  }

  // Settings
  settings: {
    title: string
    subtitle: string
    languageSection: string
    languageDesc: string
    accountSection: string
    accountDesc: string
    nameField: string
    emailField: string
    appearance: string
    appearanceDesc: string
    darkMode: string
    saved: string
  }

  // Knowledge
  knowledge: {
    title: string
    subtitle: string
    uploadFile: string
    dropFiles: string
    chunks: string
    sources: string
    empty: string
    emptyHint: string
  }

  // Calendar
  calendar: {
    title: string
    subtitle: string
    addEvent: string
    editEvent: string
    newEvent: string
    today: string
    month: string
    week: string
    titleField: string
    titlePlaceholder: string
    description: string
    allDay: string
    start: string
    end: string
    reminder: string
    min5: string
    min15: string
    min30: string
    hour1: string
    hour2: string
    day1: string
    color: string
    save: string
    saving: string
    cancel: string
    delete: string
    add: string
    noEvents: string
    reminderLabel: string
    calendar: string
    products: string
    upcomingTitle: string
    months: string[]
    weekDaysShort: string[]
  }
}

export const dictionaries: Record<Locale, Dictionary> = {
  uk: {
    common: {
      signIn: "Увійти",
      signOut: "Вийти",
      signUp: "Зареєструватись",
      getStarted: "Почати",
      loading: "Завантаження...",
      save: "Зберегти",
      cancel: "Скасувати",
      delete: "Видалити",
      edit: "Редагувати",
      create: "Створити",
      refresh: "Оновити",
      back: "Назад",
      settings: "Налаштування",
      or: "АБО",
    },
    home: {
      badge: "Live · AI-команда для українського SMB",
      headlineLine1: "AI-команда, що діє.",
      headlineLine2: "Не радить.",
      subheadline:
        "AI-Директор розмовляє з тобою, розуміє бізнес і збирає команду AI-співробітників — з доступами до твоїх API, з правом викликати запити, з журналом власних рішень. Не «ось що я б зробив», а «ось що я вже зробив».",
      ctaPrimary: "Створити робочий простір",
      ctaSecondary: "Увійти",
      trust1: "Розмовний інтерфейс",
      trust2: "Реальні API-інтеграції",
      trust3: "Твої дані — у твоїй SQLite",

      missionTitle: "Що таке AgentHub",
      missionKicker:
        "Не чат-бот. Не дашборд. Командна робота з AI, де Директор — твій партнер, а агенти — руки.",
      problemTitle: "Проблема",
      problemBody:
        "SMB-власник тоне у десятках інструментів: CRM, GA4, реклама, Instagram DM, банки, Нова Пошта, складські листи. 10+ дашбордів, нуль синтезу. Хочеться сказати «розберись» — а немає кому.",
      approachTitle: "Наш підхід",
      approachBody:
        "Один AI-Директор, що розмовляє як партнер. Бачить дані з усіх систем, ділить задачі між AI-співробітниками (Sales, Аналітик, Контент, HR, Instagram), виконує API-виклики у реальному часі, веде власну пам'ять про бізнес і себе.",
      diffTitle: "У чому різниця",
      diffBody:
        "Інші платформи кажуть «що б я зробив». AgentHub дзвонить у Nova Poshta API за трекінгом, виставляє статус у CRM, пише клієнту в Instagram, надсилає звіт у Telegram — все через прозорі tool-виклики. Ти бачиш кожну дію.",

      featuresTitle: "Що входить у команду",
      feature1Title: "AI-Директор-оркестратор",
      feature1Desc:
        "Єдина точка контакту. Делегує, перевіряє результати, звітує. Вчить нові API в розмові. Пам'ятає що ти казав тиждень тому.",
      feature2Title: "Команда з інструментами",
      feature2Desc:
        "Sales знає замовлення. Аналітик — GA4 + FB Ads. Instagram — DM і автовідповіді. Контент — студія генерації. Не чати, а виконавці.",
      feature3Title: "24+ готові інтеграції",
      feature3Desc:
        "GA4, Facebook Ads, Instagram Direct, KeyCRM, OneBox, CS-Cart, Нова Пошта, ПриватБанк, Telegram, Clarity, Trello, MCP-сервери. Підключення у 2 хвилини.",
      feature4Title: "Живий арсенал",
      feature4Desc:
        "Витягнув щось з API — можеш сказати «запам'ятай це як tool». Директор зберігає шаблон і наступний раз викликає одним кроком. Твій персональний арсенал росте.",
      feature5Title: "Вчиться на собі",
      feature5Desc:
        "Веде власний journal, self-playbook з правилами поведінки. Щотижня в неділю — self-reflection cron аналізує де помилявся і пропонує нові правила.",
      feature6Title: "Multi-tenant ізоляція",
      feature6Desc:
        "Кожен тенант — у власній SQLite. Свої credentials, свій heartbeat, свої AI-співробітники. Pay-as-you-go через Monobank. Український хостинг, дані не виходять з України.",

      howTitle: "Як це працює",
      howKicker: "Від «розкажи про бізнес» до живої команди — до 10 хвилин.",
      step1Title: "Реєструйся",
      step1Body:
        "Створи робочий простір за хвилину. Директор одразу вітає і питає про твій бізнес у вільній розмові — без форм і анкет.",
      step2Title: "Підключи API",
      step2Body:
        "Google Analytics, Facebook Ads, Instagram, CRM, банк, ТТН — по одному, без коду. Director бачить що підключено в реальному часі.",
      step3Title: "Отримай команду",
      step3Body:
        "Директор сам створює AI-співробітників з правильними ролями та інструментами під твій бізнес. Перші звіти — за кілька хвилин.",
      step4Title: "Працюй як з живими",
      step4Body:
        "Пиши Директору задачі. Команда виконує, Director звітує. Щоранку brief, щотижня звіт, автоматичне сповіщення про аномалії.",

      closingTitle: "Готовий спробувати?",
      closingBody:
        "Безкоштовна реєстрація. Pay-as-you-go — платиш лише за виклики до LLM, не за підписку. Перший звіт — за 10 хвилин.",
      closingCta: "Почати безкоштовно",

      footer: "AgentHub · AI-команда для українського SMB · Український хостинг",
    },
    auth: {
      welcomeBack: "З поверненням",
      welcomeBackSub: "Увійдіть до вашого AgentHub workspace",
      createWorkspace: "Створіть свій простір",
      createWorkspaceSub: "Зберіть AI команду за хвилини",
      nameLabel: "Ваше ім'я",
      namePlaceholder: "Дмитро Павлик",
      emailLabel: "Email",
      emailPlaceholder: "you@company.com",
      passwordLabel: "Пароль",
      passwordPlaceholder: "••••••••",
      passwordPlaceholderRegister: "Мінімум 8 символів",
      signInBtn: "Увійти",
      signInLoading: "Входимо...",
      createBtn: "Створити простір",
      createLoading: "Створюємо ваш простір...",
      noAccount: "Немає акаунту?",
      haveAccount: "Вже маєте акаунт?",
      loginError: "Помилка входу. Перевірте email та пароль.",
      registerError: "Помилка реєстрації. Спробуйте ще раз.",
      featuresAiDirector: "AI Директор",
      featuresAutoAgents: "Авто-агенти",
      featuresPayAsGo: "Оплата за usage",
    },
    sidebar: {
      platform: "Платформа",
      overview: "Завдання",
      directorChat: "Чат з Директором",
      workspace: "Робочий простір",
      knowledgeBase: "База знань",
      memory: "Пам'ять",
      schedule: "Планувальник",
      integrations: "Інтеграції",
      finance: "Фінанси",
      usage: "Використання",
      settings: "Налаштування",
      yourAgents: "Ваші агенти",
      noAgents: "Поки немає агентів.",
      startWithDirector: "Почати з Директора →",
      addAgent: "Додати агента через Директора",
      workspaceOwner: "Власник простору",
    },
    dashboard: {
      welcomeBack: "З поверненням",
      teamReady: "Ваша AI команда готова до роботи",
      teamWaiting: "Ваша AI команда чекає на створення",
      refresh: "Оновити",
      activeAgents: "Активні агенти",
      tasksCompleted: "Виконано задач",
      pendingTasks: "В роботі",
      needApproval: "Потребує схвалення",
      yourTeam: "Ваша команда агентів",
      askDirectorMore: "Попросіть Директора додати ще",
      requiresApproval: "Потребує вашого схвалення",
      approve: "Схвалити",
      reject: "Відхилити",
      done: "Готово",
      pending: "В роботі",
      approve_short: "Схвал.",
      open: "Відкрити",
      directorReady: "Ваш AI Директор готовий",
      directorReadySub:
        "Поспілкуйтесь з ним, щоб створити вашу команду агентів.",
      getStartedBadge: "Запуск за 5 хвилин",
      buildYourTeam: "Зберіть AI команду",
      buildTeamDesc:
        "Поспілкуйтесь з AI Директором. Опишіть бізнес — галузь, команду, цілі, болі. Директор створить команду спеціалізованих агентів (Sales, Content, Instagram, HR), адаптовану під ваші потреби.",
      startWithDirectorBtn: "Почати з AI Директора",
      tour1Title: "Зустріньте Директора",
      tour1Desc: "Розкажіть AI Директору про ваш бізнес і цілі",
      tour2Title: "Отримайте команду",
      tour2Desc: "Директор автоматично створить спеціалізованих агентів",
      tour3Title: "Спостерігайте за роботою",
      tour3Desc: "Агенти виконують задачі, надсилають DM, генерують контент",
    },
    settings: {
      title: "Налаштування",
      subtitle: "Керуйте налаштуваннями вашого workspace",
      languageSection: "Мова",
      languageDesc: "Виберіть мову інтерфейсу AgentHub",
      accountSection: "Акаунт",
      accountDesc: "Інформація про ваш акаунт",
      nameField: "Ім'я",
      emailField: "Email",
      appearance: "Зовнішній вигляд",
      appearanceDesc: "Темна тема за замовчуванням",
      darkMode: "Темна тема",
      saved: "Збережено",
    },
    knowledge: {
      title: "База знань",
      subtitle:
        "Ваші бізнес-дані, які агенти використовують для контекстних відповідей",
      uploadFile: "Завантажити файл",
      dropFiles: "Перетягніть файли сюди або клікніть для вибору",
      chunks: "Фрагментів",
      sources: "Джерел",
      empty: "База знань порожня",
      emptyHint: "Поспілкуйтеся з Директором — він додасть контекст автоматично",
    },
    calendar: {
      title: "Календар подій",
      subtitle: "Плануйте події та отримуйте нагадування",
      addEvent: "Додати подію",
      editEvent: "Редагувати подію",
      newEvent: "Нова подія",
      today: "Сьогодні",
      month: "Місяць",
      week: "Тиждень",
      titleField: "Назва",
      titlePlaceholder: "Зустріч з клієнтом",
      description: "Опис",
      allDay: "Весь день",
      start: "Початок",
      end: "Кінець",
      reminder: "Нагадування",
      min5: "За 5 хвилин",
      min15: "За 15 хвилин",
      min30: "За 30 хвилин",
      hour1: "За 1 годину",
      hour2: "За 2 години",
      day1: "За 1 день",
      color: "Колір",
      save: "Зберегти",
      saving: "Збереження...",
      cancel: "Скасувати",
      delete: "Видалити",
      add: "Додати",
      noEvents: "Немає подій",
      reminderLabel: "Нагадування",
      calendar: "Календар",
      products: "Продукти",
      upcomingTitle: "Найближчі події",
      months: ["Січень", "Лютий", "Березень", "Квітень", "Травень", "Червень", "Липень", "Серпень", "Вересень", "Жовтень", "Листопад", "Грудень"],
      weekDaysShort: ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"],
    },
  },
  en: {
    common: {
      signIn: "Sign in",
      signOut: "Sign out",
      signUp: "Sign up",
      getStarted: "Get started",
      loading: "Loading...",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      create: "Create",
      refresh: "Refresh",
      back: "Back",
      settings: "Settings",
      or: "OR",
    },
    home: {
      badge: "Live · AI team for SMBs",
      headlineLine1: "An AI team that acts.",
      headlineLine2: "Not just advises.",
      subheadline:
        "The AI Director talks with you, learns your business, and assembles a team of AI employees — each with access to your APIs, the right to call requests, and a journal of their own decisions. Not \"here's what I'd do\" — here's what I've already done.",
      ctaPrimary: "Create your workspace",
      ctaSecondary: "Sign in",
      trust1: "Conversational interface",
      trust2: "Real API integrations",
      trust3: "Your data in your SQLite",

      missionTitle: "What is AgentHub",
      missionKicker:
        "Not a chatbot. Not a dashboard. Collaborative work with AI — where the Director is your partner and the agents are the hands.",
      problemTitle: "The problem",
      problemBody:
        "SMB owners drown in dozens of tools: CRM, GA4, ads, Instagram DMs, banks, shipping, stockouts. 10+ dashboards, zero synthesis. You want to say \"sort it out\" — but there's no one to say it to.",
      approachTitle: "Our approach",
      approachBody:
        "One AI Director who speaks like a partner. Sees data across every system, delegates to AI employees (Sales, Analytics, Content, HR, Instagram), calls APIs in real time, maintains its own memory about your business and its own behavior.",
      diffTitle: "What's different",
      diffBody:
        "Other platforms tell you what they would do. AgentHub calls the Nova Poshta API for tracking, sets the CRM status, replies to the Instagram client, sends the Telegram report — all through transparent tool calls. You see every action.",

      featuresTitle: "What's in the team",
      feature1Title: "AI Director orchestrator",
      feature1Desc:
        "Single point of contact. Delegates, verifies results, reports back. Learns new APIs through conversation. Remembers what you said last week.",
      feature2Title: "Team with tools",
      feature2Desc:
        "Sales knows your orders. Analyst — GA4 + FB Ads. Instagram — DMs and auto-replies. Content — generation studio. Not chats, but executors.",
      feature3Title: "24+ ready integrations",
      feature3Desc:
        "GA4, Facebook Ads, Instagram Direct, KeyCRM, OneBox, CS-Cart, Nova Poshta, PrivatBank, Telegram, Clarity, Trello, MCP servers. Connect in 2 minutes.",
      feature4Title: "Living arsenal",
      feature4Desc:
        "Pulled something from an API? Say \"save this as a tool\". The Director stores the template and next time calls it in one step. Your personal arsenal grows.",
      feature5Title: "Learns from itself",
      feature5Desc:
        "Keeps its own journal, self-playbook of behavior rules. Every Sunday — a self-reflection cron analyzes where it went wrong and proposes new rules.",
      feature6Title: "Multi-tenant isolation",
      feature6Desc:
        "Every tenant in its own SQLite. Own credentials, own heartbeat, own AI employees. Pay-as-you-go via Monobank. Ukrainian hosting — your data stays in Ukraine.",

      howTitle: "How it works",
      howKicker: "From \"tell me about your business\" to a live team — under 10 minutes.",
      step1Title: "Sign up",
      step1Body:
        "Create your workspace in a minute. Director greets you and asks about your business in free conversation — no forms, no questionnaires.",
      step2Title: "Connect APIs",
      step2Body:
        "Google Analytics, Facebook Ads, Instagram, CRM, bank, shipping — one by one, no code. Director sees what's connected in real time.",
      step3Title: "Get your team",
      step3Body:
        "Director creates AI employees with the right roles and tools for your business. First reports arrive in minutes.",
      step4Title: "Work as with humans",
      step4Body:
        "Give the Director tasks. The team executes, Director reports. Daily morning brief, weekly report, automatic anomaly alerts.",

      closingTitle: "Ready to try?",
      closingBody:
        "Free signup. Pay-as-you-go — you pay only for LLM calls, not a subscription. First report arrives in 10 minutes.",
      closingCta: "Start free",

      footer: "AgentHub · AI team for SMBs · Ukrainian hosting",
    },
    auth: {
      welcomeBack: "Welcome back",
      welcomeBackSub: "Sign in to your AgentHub workspace",
      createWorkspace: "Create your workspace",
      createWorkspaceSub: "Build your AI team in minutes",
      nameLabel: "Your name",
      namePlaceholder: "Dmytro Pavlyk",
      emailLabel: "Email",
      emailPlaceholder: "you@company.com",
      passwordLabel: "Password",
      passwordPlaceholder: "••••••••",
      passwordPlaceholderRegister: "Minimum 8 characters",
      signInBtn: "Sign in",
      signInLoading: "Signing in...",
      createBtn: "Create workspace",
      createLoading: "Creating your workspace...",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      loginError: "Login failed. Please check your credentials.",
      registerError: "Registration failed. Please try again.",
      featuresAiDirector: "AI Director",
      featuresAutoAgents: "Auto-agents",
      featuresPayAsGo: "Pay as go",
    },
    sidebar: {
      platform: "Platform",
      overview: "Tasks",
      directorChat: "Director Chat",
      workspace: "Workspace",
      knowledgeBase: "Knowledge Base",
      memory: "Memory",
      schedule: "Scheduler",
      integrations: "Integrations",
      finance: "Finance",
      usage: "Usage",
      settings: "Settings",
      yourAgents: "Your agents",
      noAgents: "No agents yet.",
      startWithDirector: "Start with Director →",
      addAgent: "Add agent via Director",
      workspaceOwner: "Workspace owner",
    },
    dashboard: {
      welcomeBack: "Welcome back",
      teamReady: "Your AI team is ready to work",
      teamWaiting: "Your AI team is waiting to be assembled",
      refresh: "Refresh",
      activeAgents: "Active agents",
      tasksCompleted: "Tasks completed",
      pendingTasks: "Pending tasks",
      needApproval: "Need approval",
      yourTeam: "Your agent team",
      askDirectorMore: "Ask Director to add more",
      requiresApproval: "Requires your approval",
      approve: "Approve",
      reject: "Reject",
      done: "Done",
      pending: "Pending",
      approve_short: "Approve",
      open: "Open",
      directorReady: "Your AI Director is ready",
      directorReadySub:
        "Chat with them to create your team.",
      getStartedBadge: "Get started in 5 minutes",
      buildYourTeam: "Build your AI team",
      buildTeamDesc:
        "Chat with your AI Director. Describe your business — industry, team, goals, pain points. The Director will create a team of specialized agents (Sales, Content, Instagram, HR) tailored to your needs.",
      startWithDirectorBtn: "Start with AI Director",
      tour1Title: "Meet the Director",
      tour1Desc: "Tell the AI Director about your business and goals",
      tour2Title: "Get your team",
      tour2Desc: "Director creates specialized agents automatically",
      tour3Title: "Watch them work",
      tour3Desc: "Agents execute tasks, send DMs, generate content",
    },
    settings: {
      title: "Settings",
      subtitle: "Manage your workspace preferences",
      languageSection: "Language",
      languageDesc: "Choose your AgentHub interface language",
      accountSection: "Account",
      accountDesc: "Your account information",
      nameField: "Name",
      emailField: "Email",
      appearance: "Appearance",
      appearanceDesc: "Dark theme by default",
      darkMode: "Dark mode",
      saved: "Saved",
    },
    knowledge: {
      title: "Knowledge Base",
      subtitle: "Your business data that agents use for contextual responses",
      uploadFile: "Upload file",
      dropFiles: "Drop files here or click to select",
      chunks: "Chunks",
      sources: "Sources",
      empty: "Knowledge base is empty",
      emptyHint:
        "Chat with the Director — they will add context automatically",
    },
    calendar: {
      title: "Event Calendar",
      subtitle: "Plan events and get reminders",
      addEvent: "Add event",
      editEvent: "Edit event",
      newEvent: "New event",
      today: "Today",
      month: "Month",
      week: "Week",
      titleField: "Title",
      titlePlaceholder: "Client meeting",
      description: "Description",
      allDay: "All day",
      start: "Start",
      end: "End",
      reminder: "Reminder",
      min5: "5 minutes before",
      min15: "15 minutes before",
      min30: "30 minutes before",
      hour1: "1 hour before",
      hour2: "2 hours before",
      day1: "1 day before",
      color: "Color",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      delete: "Delete",
      add: "Add",
      noEvents: "No events",
      reminderLabel: "Reminder",
      calendar: "Calendar",
      products: "Products",
      upcomingTitle: "Upcoming events",
      months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      weekDaysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    },
  },
}
